import React from "react";
import PropTypes from "prop-types";
import { connect, Provider } from "react-redux";
import { bindActionCreators } from "redux";
import { DesignSystemProvider, theme } from "@claroty/design-system";
import { getHistory } from "@claroty/utilities/store/history";
import { AppContainerProvider } from "@claroty/xdome-design-system/providers/AppContainerProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import autoBind from "auto-bind";
import { ConnectedRouter } from "connected-react-router";
import { endLoading, restoreLogin, setDarkMode, startLoading } from "../actions/PreAuthActions/preAuthActions";
import { setIsAuthenticated } from "../actions/authActions";
import { initPostAuthStore } from "../store/initStore";
import { APP_DIV, PRE_AUTH_LOCAL_STORAGE_KEYS } from "../const/preAuthConsts";
import { getUnsupportedBrowserMessage } from "../utils/preAuthUtils";
import { LOADER_DURATION_NAMES } from "../utils/tracking";
import BrowserNotSupported from "./common/BrowserNotSupported/BrowserNotSupported";
import reactQueryClient from "../api/reactQuery/reactQueryClient";
import BaseApp from "./BaseApp";
import TranslationProvider from "./app/TranslationProvider";

class Root extends React.Component {
    constructor(props) {
        super(props);
        autoBind(this);

        this.setThemeFromLocalStorage();
        this.restoreLogin();

        this.unsupportedBrowserMessage = getUnsupportedBrowserMessage();
        this.isPostAuthStoreInitialized = false;
    }

    restoreLogin() {
        // If we were authenticated before refreshing / entering verify that we're still logged in on the server
        // We can be logged in from a previous connection or from SSO
        this.props.actions.startLoading(LOADER_DURATION_NAMES.ROOT_LOADER);
        this.props.actions
            .restoreLogin()
            .then(() => {
                this.initPostAuthStore();
            })
            .catch(() => {
                this.props.actions.setIsAuthenticated(false);
            })
            .finally(() => {
                this.props.actions.endLoading();
            });
    }

    setThemeFromLocalStorage() {
        this.props.actions.setDarkMode(localStorage.getItem(PRE_AUTH_LOCAL_STORAGE_KEYS.isDarkMode) === "true");
    }

    initPostAuthStore() {
        return initPostAuthStore(this.props.store).then(() => {
            this.isPostAuthStoreInitialized = true;
            this.props.actions.setIsAuthenticated(true);
        });
    }

    onLogin() {
        // if we are re-logging in after logging out we already have the full post auth store
        if (!this.isPostAuthStoreInitialized) this.initPostAuthStore();
        else this.props.actions.setIsAuthenticated(true);
    }

    render() {
        if (this.unsupportedBrowserMessage) return <BrowserNotSupported message={this.unsupportedBrowserMessage} />;

        const queryClientComponent = (
            <QueryClientProvider client={reactQueryClient}>
                <AppContainerProvider container={APP_DIV}>
                    <DesignSystemProvider theme={theme}>
                        <BaseApp isAuthenticated={this.props.isAuthenticated} onLogin={this.onLogin} />
                    </DesignSystemProvider>
                </AppContainerProvider>
            </QueryClientProvider>
        );

        const queryClientWithStrictMode =
            process.env.NODE_ENV === "development" ? (
                <React.StrictMode>{queryClientComponent}</React.StrictMode>
            ) : (
                queryClientComponent
            );

        return (
            <TranslationProvider isAuthenticated={this.props.isAuthenticated}>
                <Provider store={this.props.store}>
                    <ConnectedRouter history={getHistory()}>{queryClientWithStrictMode}</ConnectedRouter>
                </Provider>
            </TranslationProvider>
        );
    }
}

function mapStateToProps(state) {
    return { isAuthenticated: state.preAuth.isAuthenticated };
}

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({ setIsAuthenticated, restoreLogin, startLoading, endLoading, setDarkMode }, dispatch),
});

Root.propTypes = {
    history: PropTypes.object,
    store: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    isAuthenticated: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Root);
